/**
 * Patient Portal i18n — English + Español.
 * Simple key→string dictionary. Language preference in localStorage.
 */
import { createContext, useContext, useEffect, useState } from "react";

const STRINGS = {
  en: {
    // Auth
    auth_portal: "Patient portal",
    auth_org: "1st Touch Medical Transport",
    auth_signin: "Sign in",
    auth_register: "Create your account",
    auth_forgot_title: "Reset your password",
    auth_full_name: "Full name",
    auth_phone: "Phone (e.g. 404-555-0100)",
    auth_email: "Email address",
    auth_password: "Password",
    auth_password_min: "Password (min 8 characters)",
    auth_signin_submit: "Sign in",
    auth_register_submit: "Create account",
    auth_forgot_submit: "Send reset link",
    auth_forgot_link: "Forgot password?",
    auth_new_here: "New here?",
    auth_create_account: "Create an account",
    auth_already: "Already have one?",
    auth_back_signin: "Back to sign in",
    auth_hipaa: "Your health information is protected under HIPAA. We never share your details with anyone outside of your care team without your consent.",
    auth_welcome_back: "Welcome back!",
    auth_account_created: "Account created!",
    auth_password_short: "Password must be at least 8 characters",
    auth_password_reset_sent: "If that email exists, a reset link will be sent shortly.",
    auth_something_wrong: "Something went wrong",

    // Dashboard
    dash_hi: "Hi,",
    dash_welcome_subtitle: "Book a ride, track your driver, and manage upcoming trips.",
    dash_profile: "Profile",
    dash_wallet: "Wallet",
    dash_signout: "Sign out",
    dash_book_title: "Book a ride",
    dash_book_subtitle: "Schedule a new NEMT trip · at least 24 hours out",
    dash_finish_profile_title: "Finish your profile",
    dash_finish_profile_body: "Add your Medicaid ID, doctor, and home address so booking is one tap.",
    dash_finish_profile_cta: "Update →",
    dash_upcoming: "Upcoming rides",
    dash_past: "Past rides",
    dash_no_rides: "No upcoming rides yet. Book one above.",
    dash_loading: "Loading…",

    // Wallet
    wallet_title: "Payment methods",
    wallet_subtitle: "Manage the cards saved to your profile. Your default card is used for holds on new bookings.",
    wallet_empty: "No cards on file yet. Add one now to unlock 1-tap booking.",
    wallet_add_cta: "Add a new card",

    // Book
    book_new: "New booking",
    book_schedule: "Schedule a ride",
    book_who_paying: "Who's paying?",
    book_payer_private: "Private pay",
    book_payer_medicaid: "Medicaid",
    book_payer_medicare: "Medicare",
    book_member_id: "Member ID",
    book_plan: "Plan name",
    book_broker: "Broker (if applicable)",
    book_trip_type: "Trip type",
    book_pickup: "Pickup",
    book_pickup_ph: "Home or facility address",
    book_dropoff: "Destination",
    book_dropoff_ph: "Clinic, hospital, or appointment address",
    book_when: "When?",
    book_24h: "Please book at least 24 hours in advance.",
    book_mobility: "Mobility & notes",
    book_purpose_ph: "Purpose (e.g. dialysis, cardiology, physical therapy)",
    book_notes_ph: "Anything the driver should know? (oxygen tank, service animal, escort ride-along, etc.)",
    book_hold_line: (amt) => `Card hold: ${amt}. We won't charge you until dispatch reviews the actual trip. Final amount is captured after the ride.`,
    book_submit_private: (amt) => `Continue to secure payment (${amt} hold)`,
    book_submit_ins: "Submit ride request",
    book_fill_required: "Fill in pickup, destination, date and time.",
    book_success: "Ride request received — dispatch will confirm shortly.",
    book_failed: "Booking failed",
    book_for: "This ride is for",
    book_for_me: "Me",

    // Mobility labels
    mob_ambulatory: "Ambulatory",
    mob_wheelchair: "Wheelchair",
    mob_stretcher: "Stretcher",
    mob_assist_only: "Assist only",

    // Trip class labels
    tc_local_oneway: "Local one-way",
    tc_roundtrip_under10: "Round trip <10 mi",
    tc_dialysis_rt: "Dialysis round trip",
    tc_weekly_dialysis_pkg: "Weekly dialysis package",
    tc_weekly_medical_pkg: "Weekly medical package",
    tc_discharge: "Hospital discharge (on-call)",
    tc_other: "Other / needs quote",

    // Ride detail
    ride_details: "Ride details",
    ride_status: "Status",
    ride_driver_status: "Driver",
    ride_your_driver: "Your driver",
    ride_vehicle_fallback: "Vehicle details on the way",
    ride_tag: "Tag",
    ride_trip: "Trip",
    ride_pickup_lbl: "Pickup:",
    ride_dropoff_lbl: "Destination:",
    ride_hold_lbl: "Hold:",
    ride_captured_lbl: "Captured:",
    ride_cancel_fee_lbl: "Cancellation fee:",
    ride_fee_waived: "Fee waived — thank you.",
    ride_cancel_button: "Cancel this ride",

    // Cancel
    cancel_title: "Cancel your ride",
    cancel_intro: "Let us know why so we can improve.",
    cancel_reason_doctor: "My doctor / clinic cancelled the appointment",
    cancel_reason_unwell: "I'm feeling too unwell to travel",
    cancel_reason_resched: "I need to reschedule",
    cancel_reason_other: "Other reason",
    cancel_no_fee: "No fee",
    cancel_fee_15: "$15 fee",
    cancel_detail_ph: "Anything else we should know? (optional)",
    cancel_keep: "Keep my ride",
    cancel_confirm: "Confirm cancel",
    cancel_pick_reason: "Pick a reason so dispatch understands why",
    cancel_success_waived: "Ride cancelled — no fee.",
    cancel_success_fee: (amt) => `Ride cancelled — ${amt} fee.`,

    // Profile
    profile_title: "Your profile",
    profile_name: "Full name",
    profile_phone: "Phone",
    profile_dob: "Date of birth",
    profile_home: "Home address",
    profile_home_ph: "Home address",
    profile_medicaid: "Medicaid / Insurance member #",
    profile_plan: "Insurance plan",
    profile_doctor: "Primary doctor",
    profile_doctor_ph: "Dr. Smith",
    profile_doctor_phone: "Doctor's phone",
    profile_save: "Save profile",
    profile_updated: "Profile updated.",

    // Family
    fam_section: "Family members",
    fam_subtitle: "Book rides for a spouse, parent, or child from your account.",
    fam_add: "Add a family member",
    fam_relationship_ph: "Relationship (spouse, parent, child, other)",
    fam_edit: "Edit",
    fam_remove: "Remove",
    fam_saved: "Saved.",
    fam_removed: "Removed.",
    fam_none: "No family members yet.",
    fam_confirm_remove: "Remove this family member?",

    // Common
    lang_switch: "Language",
    lang_english: "English",
    lang_spanish: "Español",

    // Recurring
    recur_toggle: "Repeat weekly (dialysis / recurring appointments)",
    recur_weeks: "Number of weeks",
    recur_days: "Days of the week",
    recur_days_hint: "Choose one or more days each week — a separate ride will be booked for each date.",
    recur_notice_medicaid_only: "Recurring bookings are currently for Medicaid or Medicare rides only.",
    recur_success: (n) => `Booked ${n} rides in this series.`,
    recur_series_badge: (i, n) => `Ride ${i} of ${n}`,
    recur_cancel_remaining: "Cancel all remaining rides in this series",
    recur_cancel_confirm: "Cancel every future ride in this series?",
    recur_cancel_success: (n) => `Cancelled ${n} remaining rides.`,

    // Reschedule
    resched_button: "Reschedule",
    resched_title: "Move this ride",
    resched_intro: "Pick a new date and time. Your card hold stays the same — no re-authorization needed.",
    resched_new_date: "New date",
    resched_new_time: "New time",
    resched_confirm: "Save new time",
    resched_success: "Ride moved — dispatch has been notified.",

    // Share ride
    share_button: "Share live tracking",
    share_title: "Share live tracking",
    share_intro: "Send this link to a family member — they'll see the driver's location, name, and car in real time. Link expires ~3 hours after the trip.",
    share_copy: "Copy link",
    share_copied: "Link copied to clipboard!",
    share_open: "Open preview",

    // Days
    day_mon: "Mon",
    day_tue: "Tue",
    day_wed: "Wed",
    day_thu: "Thu",
    day_fri: "Fri",
    day_sat: "Sat",
    day_sun: "Sun",

    // Push
    push_enable_title: "Turn on ride alerts",
    push_enable_body: "Get a phone notification the moment your driver accepts, arrives, or completes your ride.",
    push_enabled: "Alerts on",
    push_enable_cta: "Enable notifications",
    push_success: "Notifications enabled — you'll get an alert when your driver's on the way.",
    push_denied: "Notifications blocked in your browser. Enable them in settings.",

    // ETA
    eta_mins_away: (n) => `${n} min${n === 1 ? "" : "s"} away`,
    eta_at_pickup: "At pickup",

    // Series pending payment
    pay_hold_title: "Authorize your ride hold",
    pay_hold_body: (amt) => `A refundable ${amt} hold is needed to lock in this ride. It's captured only after the trip.`,
    pay_hold_cta: (amt) => `Authorize ${amt} hold`,
    pay_hold_pending_badge: "Needs card hold",

    // Ratings
    rate_title: "How was your ride?",
    rate_subtitle: "Your feedback goes straight to our dispatch team.",
    rate_stars_1: "Terrible",
    rate_stars_2: "Not great",
    rate_stars_3: "Okay",
    rate_stars_4: "Good",
    rate_stars_5: "Excellent",
    rate_comment_ph: "Anything the team should know? (optional)",
    rate_submit: "Send rating",
    rate_thanks: "Thanks for the feedback!",
    rate_your_rating: "Your rating",
    rate_edit: "Edit rating",
  },
  es: {
    // Auth
    auth_portal: "Portal del paciente",
    auth_org: "1st Touch Medical Transport",
    auth_signin: "Iniciar sesión",
    auth_register: "Crea tu cuenta",
    auth_forgot_title: "Restablece tu contraseña",
    auth_full_name: "Nombre completo",
    auth_phone: "Teléfono (ej. 404-555-0100)",
    auth_email: "Correo electrónico",
    auth_password: "Contraseña",
    auth_password_min: "Contraseña (mínimo 8 caracteres)",
    auth_signin_submit: "Iniciar sesión",
    auth_register_submit: "Crear cuenta",
    auth_forgot_submit: "Enviar enlace",
    auth_forgot_link: "¿Olvidaste tu contraseña?",
    auth_new_here: "¿Eres nuevo?",
    auth_create_account: "Crear una cuenta",
    auth_already: "¿Ya tienes una cuenta?",
    auth_back_signin: "Volver a iniciar sesión",
    auth_hipaa: "Tu información médica está protegida bajo HIPAA. Nunca compartimos tus datos con nadie fuera de tu equipo de cuidado sin tu consentimiento.",
    auth_welcome_back: "¡Bienvenido de nuevo!",
    auth_account_created: "¡Cuenta creada!",
    auth_password_short: "La contraseña debe tener al menos 8 caracteres",
    auth_password_reset_sent: "Si ese correo existe, enviaremos un enlace de restablecimiento pronto.",
    auth_something_wrong: "Algo salió mal",

    // Dashboard
    dash_hi: "Hola,",
    dash_welcome_subtitle: "Reserva un viaje, sigue a tu conductor y gestiona tus viajes.",
    dash_profile: "Perfil",
    dash_wallet: "Cartera",
    dash_signout: "Cerrar sesión",
    dash_book_title: "Reservar un viaje",
    dash_book_subtitle: "Programa un nuevo viaje NEMT · al menos 24 horas antes",
    dash_finish_profile_title: "Completa tu perfil",
    dash_finish_profile_body: "Añade tu número de Medicaid, doctor y dirección para reservar con un toque.",
    dash_finish_profile_cta: "Actualizar →",
    dash_upcoming: "Próximos viajes",
    dash_past: "Viajes anteriores",
    dash_no_rides: "Aún no tienes viajes. Reserva uno arriba.",
    dash_loading: "Cargando…",

    // Wallet
    wallet_title: "Métodos de pago",
    wallet_subtitle: "Administra las tarjetas guardadas en tu perfil. La tarjeta predeterminada se usa para las retenciones de nuevas reservas.",
    wallet_empty: "Aún no hay tarjetas guardadas. Agrega una para activar la reserva con un toque.",
    wallet_add_cta: "Agregar una tarjeta",

    // Book
    book_new: "Nueva reserva",
    book_schedule: "Programar un viaje",
    book_who_paying: "¿Quién paga?",
    book_payer_private: "Pago privado",
    book_payer_medicaid: "Medicaid",
    book_payer_medicare: "Medicare",
    book_member_id: "Número de miembro",
    book_plan: "Nombre del plan",
    book_broker: "Broker (si aplica)",
    book_trip_type: "Tipo de viaje",
    book_pickup: "Recogida",
    book_pickup_ph: "Casa o dirección del centro",
    book_dropoff: "Destino",
    book_dropoff_ph: "Clínica, hospital o dirección de la cita",
    book_when: "¿Cuándo?",
    book_24h: "Por favor reserva al menos 24 horas antes.",
    book_mobility: "Movilidad y notas",
    book_purpose_ph: "Propósito (ej. diálisis, cardiología, fisioterapia)",
    book_notes_ph: "¿Algo que el conductor debe saber? (tanque de oxígeno, animal de servicio, acompañante, etc.)",
    book_hold_line: (amt) => `Retención en tarjeta: ${amt}. No cobraremos hasta que despacho revise el viaje real. El monto final se cobra después del viaje.`,
    book_submit_private: (amt) => `Continuar al pago seguro (retención de ${amt})`,
    book_submit_ins: "Enviar solicitud de viaje",
    book_fill_required: "Completa recogida, destino, fecha y hora.",
    book_success: "Solicitud de viaje recibida — despacho confirmará pronto.",
    book_failed: "La reserva falló",
    book_for: "Este viaje es para",
    book_for_me: "Yo",

    // Mobility
    mob_ambulatory: "Ambulatorio",
    mob_wheelchair: "Silla de ruedas",
    mob_stretcher: "Camilla",
    mob_assist_only: "Solo asistencia",

    // Trip class
    tc_local_oneway: "Local ida",
    tc_roundtrip_under10: "Ida y vuelta <10 mi",
    tc_dialysis_rt: "Diálisis ida y vuelta",
    tc_weekly_dialysis_pkg: "Paquete semanal de diálisis",
    tc_weekly_medical_pkg: "Paquete semanal médico",
    tc_discharge: "Alta hospitalaria (en espera)",
    tc_other: "Otro / requiere cotización",

    // Ride detail
    ride_details: "Detalles del viaje",
    ride_status: "Estado",
    ride_driver_status: "Conductor",
    ride_your_driver: "Tu conductor",
    ride_vehicle_fallback: "Detalles del vehículo en camino",
    ride_tag: "Placa",
    ride_trip: "Viaje",
    ride_pickup_lbl: "Recogida:",
    ride_dropoff_lbl: "Destino:",
    ride_hold_lbl: "Retención:",
    ride_captured_lbl: "Cobrado:",
    ride_cancel_fee_lbl: "Cargo de cancelación:",
    ride_fee_waived: "Cargo eliminado — gracias.",
    ride_cancel_button: "Cancelar este viaje",

    // Cancel
    cancel_title: "Cancelar tu viaje",
    cancel_intro: "Cuéntanos por qué para mejorar.",
    cancel_reason_doctor: "Mi doctor / clínica canceló la cita",
    cancel_reason_unwell: "Me siento demasiado mal para viajar",
    cancel_reason_resched: "Necesito reprogramar",
    cancel_reason_other: "Otro motivo",
    cancel_no_fee: "Sin cargo",
    cancel_fee_15: "Cargo de $15",
    cancel_detail_ph: "¿Algo más que debemos saber? (opcional)",
    cancel_keep: "Mantener mi viaje",
    cancel_confirm: "Confirmar cancelación",
    cancel_pick_reason: "Elige un motivo para que despacho entienda",
    cancel_success_waived: "Viaje cancelado — sin cargo.",
    cancel_success_fee: (amt) => `Viaje cancelado — cargo de ${amt}.`,

    // Profile
    profile_title: "Tu perfil",
    profile_name: "Nombre completo",
    profile_phone: "Teléfono",
    profile_dob: "Fecha de nacimiento",
    profile_home: "Dirección de casa",
    profile_home_ph: "Dirección de casa",
    profile_medicaid: "Número de Medicaid / seguro",
    profile_plan: "Plan de seguro",
    profile_doctor: "Doctor principal",
    profile_doctor_ph: "Dr. Smith",
    profile_doctor_phone: "Teléfono del doctor",
    profile_save: "Guardar perfil",
    profile_updated: "Perfil actualizado.",

    // Family
    fam_section: "Familiares",
    fam_subtitle: "Reserva viajes para tu pareja, padre, madre o hijo desde tu cuenta.",
    fam_add: "Añadir un familiar",
    fam_relationship_ph: "Relación (pareja, padre, madre, hijo, otro)",
    fam_edit: "Editar",
    fam_remove: "Eliminar",
    fam_saved: "Guardado.",
    fam_removed: "Eliminado.",
    fam_none: "Aún no hay familiares.",
    fam_confirm_remove: "¿Eliminar este familiar?",

    // Common
    lang_switch: "Idioma",
    lang_english: "English",
    lang_spanish: "Español",

    // Recurring
    recur_toggle: "Repetir semanalmente (diálisis / citas recurrentes)",
    recur_weeks: "Número de semanas",
    recur_days: "Días de la semana",
    recur_days_hint: "Elige uno o más días de cada semana — reservaremos un viaje por cada fecha.",
    recur_notice_medicaid_only: "Las reservas recurrentes están disponibles solo para viajes de Medicaid o Medicare.",
    recur_success: (n) => `Se reservaron ${n} viajes en esta serie.`,
    recur_series_badge: (i, n) => `Viaje ${i} de ${n}`,
    recur_cancel_remaining: "Cancelar todos los viajes restantes de esta serie",
    recur_cancel_confirm: "¿Cancelar todos los viajes futuros de esta serie?",
    recur_cancel_success: (n) => `Se cancelaron ${n} viajes restantes.`,

    // Reschedule
    resched_button: "Reprogramar",
    resched_title: "Mover este viaje",
    resched_intro: "Elige una nueva fecha y hora. La retención de tu tarjeta no cambia — no hace falta re-autorizar.",
    resched_new_date: "Nueva fecha",
    resched_new_time: "Nueva hora",
    resched_confirm: "Guardar nueva hora",
    resched_success: "Viaje reprogramado — despacho ha sido notificado.",

    // Share ride
    share_button: "Compartir seguimiento en vivo",
    share_title: "Compartir seguimiento en vivo",
    share_intro: "Envía este enlace a un familiar — verá la ubicación, nombre y auto del conductor en tiempo real. Expira ~3 horas después del viaje.",
    share_copy: "Copiar enlace",
    share_copied: "¡Enlace copiado al portapapeles!",
    share_open: "Ver vista previa",

    // Days
    day_mon: "Lun",
    day_tue: "Mar",
    day_wed: "Mié",
    day_thu: "Jue",
    day_fri: "Vie",
    day_sat: "Sáb",
    day_sun: "Dom",

    // Push
    push_enable_title: "Activar alertas de viaje",
    push_enable_body: "Recibe una notificación cuando tu conductor acepte, llegue o complete tu viaje.",
    push_enabled: "Alertas activadas",
    push_enable_cta: "Activar notificaciones",
    push_success: "Notificaciones activadas — te avisaremos cuando tu conductor esté en camino.",
    push_denied: "Notificaciones bloqueadas en tu navegador. Actívalas en la configuración.",

    // ETA
    eta_mins_away: (n) => `A ${n} minuto${n === 1 ? "" : "s"}`,
    eta_at_pickup: "En el punto de recogida",

    // Series pending payment
    pay_hold_title: "Autoriza la retención",
    pay_hold_body: (amt) => `Se necesita una retención reembolsable de ${amt} para confirmar este viaje. Se cobra solo después del viaje.`,
    pay_hold_cta: (amt) => `Autorizar retención de ${amt}`,
    pay_hold_pending_badge: "Necesita retención",

    // Ratings
    rate_title: "¿Cómo fue tu viaje?",
    rate_subtitle: "Tu comentario va directo a nuestro equipo de despacho.",
    rate_stars_1: "Terrible",
    rate_stars_2: "Regular",
    rate_stars_3: "Aceptable",
    rate_stars_4: "Bueno",
    rate_stars_5: "Excelente",
    rate_comment_ph: "¿Algo que deba saber el equipo? (opcional)",
    rate_submit: "Enviar calificación",
    rate_thanks: "¡Gracias por tu comentario!",
    rate_your_rating: "Tu calificación",
    rate_edit: "Editar calificación",
  },
};

const LangContext = createContext(null);
const LS_KEY = "patient_lang";

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || "en"; } catch { return "en"; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, lang); } catch { /* ignore */ }
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
  }, [lang]);

  const t = (key, ...args) => {
    const dict = STRINGS[lang] || STRINGS.en;
    const val = dict[key] !== undefined ? dict[key] : STRINGS.en[key];
    if (typeof val === "function") return val(...args);
    return val !== undefined ? val : key;
  };

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used within LangProvider");
  return ctx;
}
